import { log, logException } from './helper/logging';
import { Severity } from '@sentry/types';
import Config from '../shared/config';
import FlutterBridge from '../flutter/flutterBridge';
import { Message } from '../store/Chatbot.store';

const { apiUrl, correlationId, isApp, deupaiApiUrl } = Config.getConfig();

export type DownloadConfig = {
    objectUrl: ReturnType<typeof window.URL.createObjectURL>;
    filename: string;
    originalUrl?: string;
};

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchRetry(url: string, options: RequestInit = {}, retries = 1, backoff = 1000): Promise<Response> {
    const retryCodes = [408];
    const response = await fetch(url, options);

    if (response.ok) return response;

    if (retries > 0 && retryCodes.includes(response.status)) {
        await delay(backoff);
        return fetchRetry(url, options, retries - 1, backoff * 2); /* 3 */
    } else {
        let errorMessage = response.statusText;
        let parsedBody: unknown;

        try {
            const responseText = await response.text();
            console.log('Response text:', responseText);
            console.log('Response status:', response.status);
            console.log('Response statusText:', response.statusText);
            console.log('URL:', url);
            console.log('Method:', options.method);

            if (responseText && responseText.trim()) {
                try {
                    const errorData = JSON.parse(responseText);
                    parsedBody = errorData;
                    console.log('Backend error response (JSON):', errorData);
                    if (errorData.message) {
                        errorMessage = errorData.message;
                    } else if (errorData.error) {
                        errorMessage = errorData.error;
                    } else if (errorData.detail) {
                        errorMessage = errorData.detail;
                    } else {
                        errorMessage = responseText;
                    }
                } catch (jsonError) {
                    if (responseText.includes('DUPLICATE_ERROR: Silo-Name/-Nummer bereits vergeben')) {
                        errorMessage = 'DUPLICATE_ERROR: Silo-Name/-Nummer bereits vergeben';
                    } else if (responseText.includes('PARTNER_ERROR: Kundennummer unbekannt')) {
                        errorMessage = 'PARTNER_ERROR: Kundennummer unbekannt';
                    } else if (responseText.includes('VALIDATION_ERROR: Name/Nummer ist erforderlich')) {
                        errorMessage = 'VALIDATION_ERROR: Name/Nummer ist erforderlich';
                    } else if (responseText.includes('VALIDATION_ERROR: Kapazität muss > 0 sein')) {
                        errorMessage = 'VALIDATION_ERROR: Kapazität muss > 0 sein';
                    } else if (responseText.includes('VALIDATION_ERROR: Initialer Füllstand darf Kapazität nicht überschreiten')) {
                        errorMessage = 'VALIDATION_ERROR: Initialer Füllstand darf Kapazität nicht überschreiten';
                    } else {
                        if (responseText.includes('Silo-Name/-Nummer bereits vergeben')) {
                            errorMessage = 'Silo-Name/-Nummer bereits vergeben';
                        } else if (responseText.includes('Kundennummer unbekannt')) {
                            errorMessage = 'Kundennummer unbekannt';
                        } else if (responseText.includes('Name/Nummer ist erforderlich')) {
                            errorMessage = 'Name/Nummer ist erforderlich';
                        } else if (responseText.includes('Kapazität muss > 0 sein')) {
                            errorMessage = 'Kapazität muss > 0 sein';
                        } else if (responseText.includes('Initialer Füllstand darf Kapazität nicht überschreiten')) {
                            errorMessage = 'Initialer Füllstand darf Kapazität nicht überschreiten';
                        } else {
                            errorMessage = responseText;
                        }
                    }
                }
            }
        } catch (e) {
            console.log('Could not get response text, using statusText:', response.statusText);
        }

        // Prefer HTTP 409 for duplicate conflicts on silo endpoints
        if (response.status === 409 && url.includes('/silo') && (options.method === 'POST' || options.method === 'PUT')) {
            errorMessage = 'DUPLICATE_ERROR: Silo-Name/-Nummer bereits vergeben';
        }

        log('fetchRetry failed', Severity.Error, {
            statusText: response.statusText,
            status: response.status,
            url: response.url,
            redirected: response.redirected,
            type: response.type,
            headers: JSON.stringify(response.headers),
            retries: retries,
            errorMessage: errorMessage,
        });

        const customError: Error & {
            status?: number;
            statusText?: string;
            response?: { status: number; data: unknown };
        } = new Error(errorMessage);
        customError.name = 'HttpError';
        customError.status = response.status;
        customError.statusText = response.statusText;
        if (parsedBody !== undefined) {
            customError.response = {
                status: response.status,
                data: parsedBody,
            };
        }
        throw customError;
    }
}

async function postData(endpoint = '', data = {}, method = 'POST'): Promise<unknown> {
    // Default options are marked with *
    const options = {
        method: method, // *GET, POST, PUT, DELETE, etc.
        mode: 'cors' as RequestMode,
        credentials: 'include' as RequestCredentials,
        headers: {
            'Content-Type': 'application/json',
            'X-Correlation-ID': correlationId,
            Authorization: 'Bearer ' + sessionStorage.getItem('authentication'),
            Accept: 'application/json',
        },
        redirect: 'follow' as RequestRedirect, // manual, *follow, error
        referrerPolicy: 'no-referrer' as ReferrerPolicy, // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        body: JSON.stringify(data), // body data type must match "Content-Type" header
    };
    try {
        const response = await fetchRetry(apiUrl + endpoint, options);
        return response.json(); // parses JSON response into native JavaScript objects
    } catch (error) {
        throw error;
    }
}

// fetchApi.ts

async function postDeupaiData(data = {}): Promise<Message> {
    // Default options are marked with *
    const options = {
        method: 'POST', // *GET, POST, PUT, DELETE, etc.
        mode: 'cors' as RequestMode, // no-cors, *cors, same-origin
        //cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
        credentials: 'include' as RequestCredentials,
        headers: {
            'Content-Type': 'application/json',
            'X-Correlation-ID': correlationId,
            Authorization: 'Bearer ' + sessionStorage.getItem('authentication'),
            Accept: 'application/json',
        },
        redirect: 'follow' as RequestRedirect, // manual, *follow, error
        referrerPolicy: 'no-referrer' as ReferrerPolicy, // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        body: JSON.stringify(data), // body data type must match "Content-Type" header
    };
    const response = await fetchRetry(deupaiApiUrl + 'chatbot', options);
    return response.json(); // parses JSON response into native JavaScript objects
}

async function getData<T>(endpoint = ''): Promise<T> {
    const [data] = await getDataWithResponseHeaders<T>(endpoint);
    return data;
}

async function putData<T>(endpoint: string, data = {}): Promise<T> {
    const options: RequestInit = {
        method: 'PUT',
        mode: 'cors' as RequestMode,
        credentials: 'include' as RequestCredentials,
        headers: {
            'Content-Type': 'application/json',
            'X-Correlation-ID': correlationId,
            Authorization: 'Bearer ' + sessionStorage.getItem('authentication'),
            Accept: 'application/json',
        },
        body: JSON.stringify(data),
    };

    const response = await fetchRetry(apiUrl + endpoint, options);
    if (response.status === 204) {
        return undefined as unknown as T;
    }
    try {
        return (await response.json()) as T;
    } catch {
        return undefined as unknown as T;
    }
}

export async function markAllMessagesAsRead(): Promise<void> {
    try {
        await putData<void>('nachrichten/mark-all-read');
        console.log('Nachrichten wurden erfolgreich als gelesen markiert.');
    } catch (error) {
        console.error('Fehler beim Markieren aller Nachrichten als gelesen:', error);
    }
}

async function getDataWithResponseHeaders<T>(endpoint = ''): Promise<[T, Headers]> {
    let response: Response | null = null;
    try {
        response = await fetch(apiUrl + endpoint, {
            mode: 'cors',
            credentials: 'include' as RequestCredentials,
            headers: {
                'X-Correlation-ID': correlationId,
                Authorization: 'Bearer ' + sessionStorage.getItem('authentication'),
                Accept: 'application/json',
            },
        });
    } catch (error: unknown) {
        if (error instanceof Error) {
            logException(error, {
                method: 'getData',
                endpoint: endpoint,
            });
        }
        throw new Error();
    }
    if (!response.ok) {
        log('getData failed', Severity.Error, {
            statusText: response.statusText,
            status: response.status,
            url: response.url,
            redirected: response.redirected,
            type: response.type,
            headers: JSON.stringify(response.headers),
        });
        throw new Error(response.statusText);
    }
    return [await response.json(), response.headers];
}

async function getDataAsBlob(endpoint = ''): Promise<DownloadConfig> {
    try {
        const downloadLink = apiUrl + endpoint;
        const response = await fetch(downloadLink, {
            mode: 'cors',
            credentials: 'include' as RequestCredentials,
            headers: {
                'X-Correlation-ID': correlationId,
                Authorization: `Bearer ${sessionStorage.getItem('authentication')}`,
                Accept: 'application/octet-stream',
            },
        });
        const filename = response.headers.get('Content-Disposition')?.split('inline; filename=')[1] ?? 'unknown.txt';
        const blob = await response.blob();
        return { objectUrl: window.URL.createObjectURL(blob), filename, originalUrl: downloadLink };
    } catch (error: unknown) {
        if (error instanceof Error) {
            logException(error, {
                method: 'getData',
                endpoint: endpoint,
            });
        }
        throw new Error();
    }
}

export function triggerDownload(config: DownloadConfig): void {
    if (isApp()) {
        FlutterBridge.sendDownloadLink(config);
    } else {
        const tempAnchorTag = document.createElement('a');
        tempAnchorTag.style.display = 'none';
        tempAnchorTag.href = config.objectUrl;
        tempAnchorTag.setAttribute('download', config.filename);
        tempAnchorTag.setAttribute('data-testid', `download-button`);

        // Safari thinks _blank anchor are pop ups. We only want to set _blank
        // target if the browser does not support the HTML5 download attribute.
        // This allows you to download files in desktop safari if pop up blocking
        // is enabled.
        if (typeof tempAnchorTag.download === 'undefined') {
            tempAnchorTag.setAttribute('target', '_blank');
        }

        document.body.appendChild(tempAnchorTag);
        tempAnchorTag.click();

        // Fixes "webkit blob resource error 1"
        setTimeout((): void => {
            document.body.removeChild(tempAnchorTag);
            window.URL.revokeObjectURL(config.objectUrl);
        }, 50);
    }
}

export async function deleteData<T = void>(endpoint: string): Promise<T> {
    const options: RequestInit = {
        method: 'DELETE',
        mode: 'cors' as RequestMode,
        credentials: 'include' as RequestCredentials,
        headers: {
            'X-Correlation-ID': correlationId,
            Authorization: 'Bearer ' + sessionStorage.getItem('authentication'),
            Accept: 'application/json',
        },
    };

    const response = await fetchRetry(apiUrl + endpoint, options);
    try {
        return (await response.json()) as T;
    } catch {
        return undefined as unknown as T;
    }
}

export async function getAuftragskorbHasNew(): Promise<boolean> {
    try {
        return (await getData<boolean>('bestellungen/auftragskorb/has-new')) ?? false;
    } catch {
        return false;
    }
}

export async function postAuftragskorbAcknowledged(): Promise<void> {
    const options: RequestInit = {
        method: 'POST',
        mode: 'cors' as RequestMode,
        credentials: 'include' as RequestCredentials,
        headers: {
            'Content-Type': 'application/json',
            'X-Correlation-ID': correlationId,
            Authorization: 'Bearer ' + sessionStorage.getItem('authentication'),
            Accept: 'application/json',
        },
        body: '{}',
    };
    const response = await fetchRetry(apiUrl + 'bestellungen/auftragskorb/acknowledged', options);
    if (response.ok && response.headers.get('content-length') !== '0') {
        await response.json();
    }
}

export { getData, getDataWithResponseHeaders, getDataAsBlob, postData, postDeupaiData, putData, fetchRetry };
